import _ from 'lodash';
import { useCallback, useRef, useState } from "react";
import { useParams } from 'react-router-dom';

export default ({ isCreate: _isCreate } = {}) => {
	const origModel = useRef({});
	const [model, setModel] = useState({});
	const changeModel = useRef({});
	const deleteModel = useRef([]);
	const [errors, setErrors] = useState({});

	const jsonModel = JSON.stringify(model);
	const { id: __id } = useParams();
	let _id = __id === 'new' ? undefined : __id;
	// Caller may force isCreate
	const isCreate = _isCreate !== undefined ? _isCreate : _id === undefined;
	const id = isCreate ? undefined : (isNaN(_id) ? _id : parseInt(_id, 10));

	const hasValue = useCallback(({ key, index, name }) => {
		const empty = isCreate ? undefined : null;
		let value;
		if (name && index !== undefined) {
			value = get_array({ key, index, name });
		} else if (name) {
			value = get_value({ key, name });
		} else {
			value = model[key];
		}
		return Array.isArray(value) ? value.length > 0 : (typeof value === 'string' ? value.length > 0 : value !== empty);
	}, [JSON.stringify(changeModel.current), jsonModel]);

	const getError = useCallback(({ key, index, name }) => {
		if (key && index !== undefined) {
			return errors[key]?.[index]?.[name];
		}
		if (key && name) {
			return errors[key]?.[name];
		}
		if (key) {
			return errors[key];
		}
		return errors[name];
	}, [JSON.stringify(errors)]);

	const get_value = useCallback(({ key = 'subjects', name }) => model[key]?.[name], [JSON.stringify(changeModel.current), jsonModel]);
	const set_value = useCallback(({ key = 'subjects', name, value }) => {
		setModel(prev => {
			// Empty strings treat as null
			const empty = isCreate ? undefined : null;
			const _value = typeof value === 'string' && value.trim().length === 0 ? empty : value;
			if (_.isEqual(origModel.current[key]?.[name], _value)) {
				if (key in changeModel.current) {
					delete changeModel.current[key][name];
					const { id: _id, __local, ...test } = changeModel.current[key];
					if (!isCreate && Object.keys(test).length === 0) {
						// Delete record id used for updates
						delete changeModel.current[key].id;
					}
				}
			} else {
				if (!(key in changeModel.current)) changeModel.current[key] = {};
				changeModel.current[key][name] = _value;
				// Add record id if updating
				if (!isCreate && 'id' in origModel.current[key]) changeModel.current[key].id = origModel.current[key].id;
			}
			if (_value !== undefined && errors[key]?.[name]) {
				setErrors(prev => ({ ...prev, [key]: { ...prev[key], [name]: undefined } }));
			}
			return { ...prev, [key]: { ...prev[key], [name]: _value } };
		})
	}, [JSON.stringify(changeModel.current), JSON.stringify(errors), jsonModel]);
	const get_array_value = useCallback(({ key = 'subjects', name }) => model[key], [JSON.stringify(changeModel.current), jsonModel]);
	const set_array_value = useCallback(({ key = 'subjects', option }) => {
		setModel(prev => {
			const empty = isCreate ? undefined : null;
			const _value = option.length === 0 ? empty : _.sortBy(option, e => e.name);
			if (_.isEqual(origModel.current[key], _value)) {
				delete changeModel.current[key];
			} else {
				if (!(key in changeModel.current)) changeModel.current[key] = [];
				changeModel.current[key] = _value;
			}
			if (_value && _value.length > 0 && errors[key]) {
				setErrors(prev => ({ ...prev, [key]: undefined }));
			}
			return { ...prev, [key]: _value };
		})
	}, [JSON.stringify(changeModel.current), JSON.stringify(errors), jsonModel]);
	const get_array = useCallback(({ key, index, name }) => {
		const a = model[key] || [];
		const ci = (changeModel.current[key] || []).findIndex(e => e.__local === a[index]);
		return ci !== -1 && 'name' in changeModel.current[key][ci] ? changeModel.current[key][ci][name] : model[key][index][name];
	}, [JSON.stringify(changeModel.current), jsonModel]);
	const set_array = useCallback(({ key, index, name, value }) => {
		const ret_promise = new Promise(resolve => {
			setModel(prev => {
				// Empty strings treat as null
				const _value = typeof value === 'string' && value.trim().length === 0 ? null : value;
				const a = prev[key] || [];
				a[index][name] = _value;
				let ci = (changeModel.current[key] || []).findIndex(e => e.__local === a[index]);
				let is_changed = false;
				if (a[index].__orig && _.isEqual(a[index].__orig[name], _value)) {
					if (ci !== -1) {
						delete changeModel.current[key][ci][name];
						const { id: _id, __local, ...test } = changeModel.current[key][ci];
						if (Object.keys(test).length === 0) {
							changeModel.current[key].splice(ci, 1);
							if (changeModel.current[key].length === 0) {
								delete changeModel.current[key];
							}
						}
					}
				} else {
					is_changed = true;
					if (!(key in changeModel.current)) {
						changeModel.current[key] = [];
					}
					if (ci === -1) {
						const len = changeModel.current[key].push({ [name]: _value, __local: a[index] });
						ci = len - 1;
					} else {
						changeModel.current[key][ci][name] = _value;
					}
					if (_value !== undefined && errors[key]?.[index]?.[name]) {
						errors[key][index][name] = undefined;
						setErrors(errors);
					}
					// Add record id if updating
					if (!isCreate && a[index].__orig && 'id' in a[index].__orig) changeModel.current[key][ci].id = a[index].__orig.id;
				}
				resolve({ is_changed });
				return { ...prev, [key]: a };
			});
		});
		return ret_promise;
	}, [JSON.stringify(errors), JSON.stringify(changeModel.current), jsonModel]);
	const add_array = useCallback(({ key, data, first }) => {
		setModel(prev => {
			const a = prev[key] || [];
			if (first) {
				a.unshift(data);
			} else {
				a.push(data);
			}
			if (!(key in changeModel.current)) changeModel.current[key] = [];
			changeModel.current[key].push({ ...data, __local: data });
			if (['subject_phone_numbers', 'subject_emails'].indexOf(key) !== -1 && errors.need_contact_info) {
				setErrors(prev => ({ ...prev, need_contact_info: undefined }));
			}
			return { ...prev, [key]: a };
		});
	}, [JSON.stringify(errors), JSON.stringify(changeModel.current), jsonModel]);
	const remove_array = useCallback(({ all = false, key, idKey = 'id', index, value, valueKey = 'id' }) => {
		setModel(prev => {
			const a = prev[key] || [];
			if (all) {
				a.forEach(e => {
					// Save an indicator for server-side delete
					deleteModel.current.push({
						id: e.id,
						key
					});
				});
				// Remove all items
				return { ...prev, [key]: [] };
			}
			// If value is supplied, look up index
			let _index;
			if (value !== undefined) {
				_index = a.findIndex(e => e.id === value.id);
			} else {
				_index = index;
			}
			const _value = value !== undefined ? value : a[_index];
			if ('id' in _value) {
				// Save an indicator for server-side delete
				deleteModel.current.push({
					id: a[_index].id,
					key
				});
			}
			const ci = (changeModel.current[key] || []).findIndex(e => e.__local === a[_index]);
			if (ci !== -1) {
				changeModel.current[key].splice(ci, 1);
				if (key in errors) {
					errors[key].splice(ci, 1);
					setErrors(errors);
				}
			}
			if (key in changeModel.current && changeModel.current[key].length === 0) {
				delete changeModel.current[key];
			}
			a.splice(_index, 1);
			if (a.length === 0) {
				delete prev[key];
				return { ...prev };
			}
			return { ...prev, [key]: a };
		});
	}, [JSON.stringify(errors), JSON.stringify(changeModel.current), jsonModel]);

	const sanitizeObject = (obj, keyToRemove = '__orig') => {
		if (_.isArray(obj)) {
		  // If the value is an array, sanitize each element
		  return obj.map(item => sanitizeObject(item, keyToRemove));
		} else if (_.isObject(obj)) {
		  // If the value is an object, sanitize recursively
		  return _.omitBy(
			_.mapValues(obj, value => sanitizeObject(value, keyToRemove)),
			(value, key) => key === keyToRemove
		  );
		}
		// If the value is neither an array nor an object, return it as-is
		return obj;
	};
	
	return {
		origModel,
		model,
		setModel,
		changeModel,
		deleteModel,
		errors,
		setErrors,
		hasValue,
		getError,
		get_value,
		set_value,
		get_array_value,
		set_array_value,
		get_array,
		set_array,
		add_array,
		remove_array,
		id,
		isCreate,
		sanitizeObject
	};
};
